import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QRCodeCanvas } from "qrcode.react";
import { Loader2, Download, Mail } from "lucide-react";
import { toast } from "sonner";
import { validateDniNie, normalizeDniNie } from "@/lib/dni";
import { generarMiQr } from "@/api";
import { buildQrPayload } from "@/lib/puntolimpioQr";
import { getErrorMessage } from "@/lib/errors";

const emptyForm = {
  nombre_apellidos: "", dni_nie: "", direccion: "", numero: "", piso: "", letra: "",
  codigopostal: "", email: "",
};

// Permite a un ciudadano generar su QR personal SIN registrar una visita (útil la
// primera vez, antes de ir físicamente al Punto Limpio). El QR se muestra al instante
// y, además, se envía por email junto con los datos que contiene.
export default function GenerarQrModal({ open, onClose }) {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [resultado, setResultado] = useState(null);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const dniValid = form.dni_nie && validateDniNie(form.dni_nie);
  const canSubmit = form.nombre_apellidos && dniValid && form.direccion && form.email;

  const handleClose = () => {
    setForm(emptyForm);
    setResultado(null);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = {
      ...form,
      dni_nie: normalizeDniNie(form.dni_nie),
      numero: form.numero ? Number(form.numero) : null,
      piso: form.piso ? Number(form.piso) : null,
      codigopostal: form.codigopostal ? Number(form.codigopostal) : null,
    };
    try {
      await generarMiQr(payload);
      setResultado(payload);
      toast.success("Tu QR está listo. También te lo hemos enviado por email.");
    } catch (err) {
      toast.error(getErrorMessage(err, "No se pudo generar tu QR. Inténtalo de nuevo."));
    } finally {
      setSubmitting(false);
    }
  };

  const downloadQr = () => {
    const canvas = document.querySelector('[data-testid="generar-qr-canvas"]');
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `mi-qr-puntolimpio-${form.dni_nie}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-md" data-testid="generar-qr-modal">
        <DialogHeader>
          <DialogTitle className="text-municipal font-heading">
            {resultado ? "Tu QR personal" : "Generar mi QR"}
          </DialogTitle>
        </DialogHeader>

        {!resultado ? (
          <form onSubmit={handleSubmit} className="space-y-3">
            <p className="text-sm text-gray-500">
              Rellena tus datos básicos una sola vez y descarga tu QR antes de ir al Punto Limpio.
              La próxima vez podrás escanearlo para autorellenar el formulario.
            </p>
            <div>
              <Label>Nombre y apellidos</Label>
              <Input value={form.nombre_apellidos} onChange={(e) => update({ nombre_apellidos: e.target.value })} data-testid="generar-qr-nombre-input" />
            </div>
            <div>
              <Label>DNI/NIE</Label>
              <Input value={form.dni_nie} onChange={(e) => update({ dni_nie: e.target.value })} data-testid="generar-qr-dni-input" />
              {form.dni_nie && !dniValid && <p className="text-xs text-red-500 mt-1">DNI/NIE no válido</p>}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Label>Dirección</Label>
                <Input value={form.direccion} onChange={(e) => update({ direccion: e.target.value })} data-testid="generar-qr-direccion-input" />
              </div>
              <div>
                <Label>Nº</Label>
                <Input value={form.numero} onChange={(e) => update({ numero: e.target.value })} data-testid="generar-qr-numero-input" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Piso</Label>
                <Input value={form.piso} onChange={(e) => update({ piso: e.target.value })} data-testid="generar-qr-piso-input" />
              </div>
              <div>
                <Label>Letra</Label>
                <Input value={form.letra} onChange={(e) => update({ letra: e.target.value })} data-testid="generar-qr-letra-input" />
              </div>
              <div>
                <Label>C.P.</Label>
                <Input value={form.codigopostal} onChange={(e) => update({ codigopostal: e.target.value })} data-testid="generar-qr-cp-input" />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => update({ email: e.target.value })} data-testid="generar-qr-email-input" />
            </div>
            <Button type="submit" disabled={!canSubmit || submitting} className="w-full bg-municipal hover:bg-municipal-dark text-white" data-testid="generar-qr-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Generar mi QR
            </Button>
          </form>
        ) : (
          <div className="flex flex-col items-center text-center py-2" data-testid="generar-qr-resultado">
            <QRCodeCanvas value={buildQrPayload(resultado)} size={180} fgColor="#BD3742" data-testid="generar-qr-canvas" />
            <p className="text-sm text-gray-500 mt-4 flex items-center gap-1.5">
              <Mail className="w-4 h-4 text-municipal" /> Enviado también a {resultado.email}
            </p>
            <Button variant="ghost" size="sm" onClick={downloadQr} className="mt-3 text-municipal" data-testid="generar-qr-download-btn">
              <Download className="w-4 h-4 mr-1" /> Descargar mi QR
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
